import React from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import { Chip, IconButton, Tooltip, Box } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RefreshIcon from '@material-ui/icons/Refresh';
import { DatabaseCluster, DatabaseEngine } from '../../types';

interface DatabaseListViewProps {
  clusters: DatabaseCluster[];
  onDelete: (namespace: string, name: string) => void;
  onPause: (namespace: string, name: string) => void;
  onResume: (namespace: string, name: string) => void;
  onRefresh: () => void;
}

const getEngineIcon = (engine: DatabaseEngine): string => {
  switch (engine) {
    case DatabaseEngine.POSTGRESQL:
      return '🐘';
    case DatabaseEngine.MYSQL:
      return '🐬';
    case DatabaseEngine.MONGODB:
      return '🍃';
    default:
      return '💾';
  }
};

const getEngineLabel = (engine: DatabaseEngine): string => {
  switch (engine) {
    case DatabaseEngine.POSTGRESQL:
      return 'PostgreSQL';
    case DatabaseEngine.MYSQL:
      return 'MySQL';
    case DatabaseEngine.MONGODB:
      return 'MongoDB';
    default:
      return engine;
  }
};

const getStatusColor = (
  status?: string,
): 'default' | 'primary' | 'secondary' => {
  switch (status) {
    case 'ready':
      return 'primary';
    case 'error':
      return 'secondary';
    default:
      return 'default';
  }
};

export const DatabaseListView: React.FC<DatabaseListViewProps> = ({
  clusters,
  onDelete,
  onPause,
  onResume,
  onRefresh,
}) => {
  const columns: TableColumn[] = [
    {
      title: 'Name',
      field: 'metadata.name',
      highlight: true,
      render: (row: any) => (
        <Box display="flex" alignItems="center">
          <span style={{ marginRight: '8px', fontSize: '20px' }}>
            {row.spec?.engine?.type ? getEngineIcon(row.spec.engine.type) : '—'}
          </span>
          <span>{row.metadata.name}</span>
        </Box>
      ),
    },
    {
      title: 'Namespace',
      field: 'metadata.namespace',
    },
    {
      title: 'Engine',
      field: 'spec.engine.type',
      render: (row: any) =>
        row.spec?.engine?.type ? getEngineLabel(row.spec.engine.type) : '—',
    },
    {
      title: 'Version',
      field: 'spec.engine.version',
    },
    {
      title: 'Status',
      field: 'status.status',
      render: (row: any) => (
        <Chip
          label={row.status?.status || 'unknown'}
          color={getStatusColor(row.status?.status)}
          size="small"
        />
      ),
    },
    {
      title: 'Replicas',
      field: 'spec.engine.replicas',
      render: (row: any) => (
        <span>
          {row.status?.ready || 0} / {row.spec?.engine?.replicas ?? '—'}
        </span>
      ),
    },
    {
      title: 'CPU',
      field: 'spec.engine.resources.cpu',
      render: (row: any) => row.spec?.engine?.resources?.cpu || 'N/A',
    },
    {
      title: 'Memory',
      field: 'spec.engine.resources.memory',
      render: (row: any) => row.spec?.engine?.resources?.memory || 'N/A',
    },
    {
      title: 'Storage',
      field: 'status.activeStorage',
      render: (row: any) => (
        <span>
          {row.status?.activeStorage || '0'} /{' '}
          {row.spec?.engine?.storage?.size || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Backup',
      field: 'spec.backup.enabled',
      render: (row: any) => (
        <Chip
          label={row.spec.backup?.enabled ? 'Enabled' : 'Disabled'}
          color={row.spec.backup?.enabled ? 'primary' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      title: 'Actions',
      field: 'actions',
      render: (row: any) => (
        <Box display="flex" style={{ gap: '8px' }}>
          {row.status?.status === 'ready' ? (
            <Tooltip title="Pause">
              <IconButton
                size="small"
                onClick={() =>
                  onPause(row.metadata.namespace, row.metadata.name)
                }
              >
                <PauseIcon />
              </IconButton>
            </Tooltip>
          ) : row.status?.status === 'paused' ? (
            <Tooltip title="Resume">
              <IconButton
                size="small"
                onClick={() =>
                  onResume(row.metadata.namespace, row.metadata.name)
                }
              >
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="secondary"
              onClick={() =>
                onDelete(row.metadata.namespace, row.metadata.name)
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Table
        title="Database Clusters"
        options={{
          paging: true,
          pageSize: 10,
          search: true,
          sorting: true,
        }}
        columns={columns}
        data={clusters}
      />
    </Box>
  );
};
